/**
 * codexIcons — turns the existing in-game biology draws into static
 * HTMLCanvasElement icons for the codex menu and toasts.
 *
 * Why: a text-only codex is hard to map back to what's actually on screen
 * during play. Rendering icons with the same draw routines used by entities
 * means a "Phagocyte Amoeba" entry shows the exact silhouette the player
 * sees in the petri dish.
 *
 * Implementation: render each icon once into an offscreen Pixi Graphics,
 * extract via the renderer to a real HTMLCanvasElement, and stash in a
 * map keyed by codex entry id. Cheap to do once at stage init.
 */

import { Application, Container, Graphics } from 'pixi.js';
import {
  drawProtein, drawSugar, drawBacteria, drawDNA,
  drawAmoeba, drawParamecium, drawDinoflagellate,
  drawCellBody,
} from './bio';
import type { PredatorDef, FoodDef } from '@/data/types';

const ICON_SIZE = 56;

function renderIcon(app: Application, draw: (g: Graphics) => void): HTMLCanvasElement {
  const c = new Container();
  // Transparent frame at fixed size forces the extract bounds to be square,
  // so every icon comes out the same dimensions regardless of its drawing.
  const frame = new Graphics();
  frame.rect(-ICON_SIZE / 2, -ICON_SIZE / 2, ICON_SIZE, ICON_SIZE)
    .fill({ color: 0x000000, alpha: 0.001 });
  c.addChild(frame);
  const g = new Graphics();
  draw(g);
  c.addChild(g);
  // Pixi v8 returns an HTMLCanvasElement here in browser contexts.
  return app.renderer.extract.canvas(c) as unknown as HTMLCanvasElement;
}

export function buildCodexIcons(
  app: Application,
  foodDefs: Record<string, FoodDef>,
  predatorDefs: Record<string, PredatorDef>,
): Map<string, HTMLCanvasElement> {
  const out = new Map<string, HTMLCanvasElement>();

  // The cell. Pure-membrane look (no mutations) so it matches the
  // freshly-spawned silhouette a new player meets first.
  out.set('concept.welcome', renderIcon(app, g => {
    drawCellBody(g, { radius: 16, fieldRadius: 28 }, 0, []);
  }));

  // Food. Each draw signature differs slightly — wrap them uniformly.
  out.set('food.protein', renderIcon(app, g => drawProtein(g, 0)));
  out.set('food.sugar', renderIcon(app, g => drawSugar(g)));
  out.set('food.bacteria', renderIcon(app, g => drawBacteria(g, 0)));
  out.set('food.dna', renderIcon(app, g => drawDNA(g, 0)));

  // Predators. Use each def's actual color so the icon matches what the
  // player sees, and shrink large radii to fit the icon frame.
  const renderPredator = (def: PredatorDef, drawFn: typeof drawAmoeba) => {
    const r = Math.min(20, def.radius);
    return renderIcon(app, g => drawFn(g, { radius: r, color: def.color }, 0));
  };

  // Map predatorId -> matching draw function (mirrors createPredator's switch).
  const drawByPredatorId: Record<string, typeof drawAmoeba> = {
    amoeba: drawAmoeba,
    paramecium_hunter: drawParamecium,
    dinoflagellate: drawDinoflagellate,
    spore_swarm: drawParamecium,
    phage_pierce: drawAmoeba,
    apex_phage: drawDinoflagellate,
  };

  for (const def of Object.values(predatorDefs)) {
    if (!def.codexId) continue;
    const drawFn = drawByPredatorId[def.id] ?? drawAmoeba;
    out.set(def.codexId, renderPredator(def, drawFn));
  }

  // Reference foodDefs param so we can extend with food-defined icons later
  // without breaking the call site signature.
  void foodDefs;

  return out;
}
